import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PersonIcon from '@mui/icons-material/Person';
import LinkIcon from '@mui/icons-material/Link';
import DescriptionIcon from '@mui/icons-material/Description';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import { DetailSkeleton } from '../components/shared/skeletons';
import { useGovernmentProjectDetailController } from './context/government-project-detail-controller';
import { useGovernmentProjectDetailState } from './context/government-project-detail-context';
import { getGovernmentalProjectTypeColor } from 'utils';

export function GovernmentProjectDetailComponent() {
  const { id } = useParams<{ id: string }>();
  const { navigateTo: navigate } = useRoutedNavigation();
  const { loadDetail } = useGovernmentProjectDetailController();
  const { detail, loading, error } = useGovernmentProjectDetailState();

  useEffect(() => {
    if (id) {
      loadDetail(Number(id));
    }
  }, [id]);

  const handleOpenLink = () => {
    if (detail?.link) {
      window.open(detail.link, '_blank');
    }
  };

  const truncateText = (text: string | undefined, maxLength: number = 60): string => {
    if (!text) return `Projeto #${id}`;
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return <DetailSkeleton hasImage={false} hasSections={1} />;
  }

  if (error || !detail) {
    return (
      <PageContainer>
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ mt: 2 }}>
            {error || 'Projeto não encontrado'}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/projetos-governamentais')}
            sx={{ mt: 2 }}
          >
            Voltar para listagem
          </Button>
        </Container>
      </PageContainer>
    );
  }

  const displayTitle = truncateText(detail.projectName, 60);

  return (
    <PageContainer>
      <Container maxWidth="xl">
        <PageHeader
          title={displayTitle}
          subtitle="Detalhes do Projeto Governamental"
          breadcrumbs={[
            { label: 'Projetos Governamentais', href: '/projetos-governamentais' },
            { label: displayTitle },
          ]}
        />

        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/projetos-governamentais')}
          sx={{ mb: 3 }}
          variant="outlined"
        >
          Voltar para listagem
        </Button>

        {/* Informações Principais */}
        <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <AccountTreeIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
              {detail.projectName}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {detail.projectTypeDescription && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Tipo de Projeto:
                  </Typography>
                  <Chip
                    label={detail.projectTypeDescription}
                    color={getGovernmentalProjectTypeColor(detail.projectType)}
                    variant="outlined"
                  />
                </Box>
              </Grid>
            )}

            {detail.responsibleName && (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <PersonIcon color="action" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Responsável pelo Projeto
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {detail.responsibleName}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {detail.link && (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LinkIcon color="action" sx={{ mt: 0.5 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Link Externo
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LinkIcon />}
                      onClick={handleOpenLink}
                      sx={{ mt: 0.5 }}
                    >
                      Acessar Link do Projeto
                    </Button>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>

          {detail.results && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <DescriptionIcon color="primary" />
                  <Typography variant="h6" color="primary">
                    Resultados e Descrição
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.8,
                  }}
                >
                  {detail.results}
                </Typography>
              </Box>
            </>
          )}
        </Paper>

        {/* Botão de ação principal */}
        {detail.link && (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                  Acesse mais informações sobre o projeto
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Clique no botão ao lado para acessar o link externo com informações detalhadas do projeto.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<LinkIcon />}
                  onClick={handleOpenLink}
                  sx={{ py: 1.5 }}
                >
                  Acessar Link Externo
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Container>
    </PageContainer>
  );
}
